import React, { ChangeEvent, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { useFormik } from 'formik';

import { SIGNUP_PATHS } from '../../../../constants';
import { useUserToken } from '../../../../hooks/userTokenHook';
import IconBtn from '../../../../modules/icon-btn/icon-btn';
import UiInput from '../../../../modules/ui/ui-input/ui-input';
import { useAppDispatch } from '../../../../store';
import { setProfileInfo } from '../../../../store/actions/signup';
import { RootState } from '../../../../types';
import FormFooter from '../form-footer/form-footer';
import FormHeader from '../form-header/form-header';
import UserIcon from '../icons/user-icon.svg';
import './registrastion-info-step.scss';

enum InfoStepLabels {
  PERSONAL_DATA = 'Ваши данные:',
  COMPANY_DATA = 'Данные компании:',
}

enum InputLabels {
  NAME = 'Имя',
  LAST_NAME = 'Фамилия',
  SECOND_NAME = 'Отчество (если есть)',
  POSITION = 'Должность',
  EMAIL = 'Email',
  INN = 'ИНН',
  OGRN = 'ОГРН',
  COMPANY_NAME = 'Полное название компании',
  COMPANY_SHORT_NAME = 'Короткое название компании',
}

export type SignupInfoFormState = {
  name: string;
  lastName: string;
  secondName: string;
  position: string;
  email: string;
  inn: string;
  ogrn: string;
  companyName: string;
  companyShortName: string;
};

const initialState: Partial<SignupInfoFormState> = {};

const InfoStep: React.FC = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { token } = useUserToken();
  const step = useSelector((s: RootState) => s.signup.step);
  const isLoadingInfoStep = useSelector(
    (s: RootState) => s.signup.loading.profileInfo
  );
  const formState = useFormik({
    initialValues: initialState,
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: (values) => {
      if (
        values.ogrn &&
        values.inn &&
        values.companyShortName &&
        values.companyName &&
        values.email &&
        values.name &&
        values.lastName &&
        values.position
      ) {
        const fullName =
          values.lastName?.trim() +
          ' ' +
          values.name?.trim() +
          (values.secondName?.trim() ? ' ' + values.secondName?.trim() : '');
        dispatch(
          setProfileInfo({
            fullName,
            position: values.position,
            email: values.email,
            company: {
              fullName: values.companyName,
              shortName: values.companyShortName,
              tin: values.inn,
              ogrn: values.ogrn,
              address: '-',
              postalAddress: '-',
              kpp: '-',
            },
          })
        ).then((action) => {
          if (action.meta.requestStatus === 'fulfilled') {
            history.push(SIGNUP_PATHS.DOCS);
          }
        });
      }
    },
    validate: (values: Partial<SignupInfoFormState>) => {
      const errors: Record<string, string> = {};

      if (values.email && !/\S+@\S+\.\S+/.test(values.email)) {
        errors.email = `Email введен неверно`;
      }

      if (
        values.inn &&
        (!Number.isInteger(Number(values.inn)) || values.inn?.length !== 10)
      ) {
        errors.inn = `ИНН должен содержать 10 цифр`;
      }

      if (
        values.ogrn &&
        (!Number.isInteger(Number(values.ogrn)) || values.ogrn.length !== 13)
      ) {
        errors.ogrn = `ОГРН должен содержать только цифры и состоять из 13 знаков`;
      }

      if (!values.companyName?.trim() && values.companyName !== undefined) {
        errors.companyName = `Поле обязательно для заполнения`;
      }

      if (
        !values.companyShortName?.trim() &&
        values.companyShortName !== undefined
      ) {
        errors.companyShortName = `Поле обязательно для заполнения`;
      }

      return errors;
    },
  });

  const getFieldError = (field: keyof SignupInfoFormState) =>
    formState.errors[field] ? (
      <div className="form-item__footer">
        <span className="red">{formState.errors[field]}</span>
      </div>
    ) : null;

  useEffect(() => {
    if (step !== 'PERSONAL_DATA_INPUT') {
      history.push(SIGNUP_PATHS.NEW);
    }

    if (!token) history.push(SIGNUP_PATHS.NEW);
  }, []);

  return (
    <form className="login-form login-form--3">
      <FormHeader bigTitle="Регистрация" />
      <div className="login-form__fildset">
        <h2 className="text-1 login-form__fildset-title ">
          {InfoStepLabels.PERSONAL_DATA}
        </h2>
        <div className="registrastion-info-step__row">
          <div className="login-form__item-input">
            <UiInput
              error={formState.errors.lastName}
              name="lastName"
              onChange={formState.handleChange}
              onInputBlur={formState.handleBlur}
              lg
              label={InputLabels.LAST_NAME}
            />
            {getFieldError('lastName')}
          </div>
          <div className="login-form__item-input">
            <UiInput
              error={formState.errors.name}
              name="name"
              onChange={formState.handleChange}
              onInputBlur={formState.handleBlur}
              lg
              label={InputLabels.NAME}
            />
            {getFieldError('name')}
          </div>
        </div>
        <div className="login-form__item-input">
          <UiInput
            error={formState.errors.secondName}
            name="secondName"
            onChange={formState.handleChange}
            onInputBlur={formState.handleBlur}
            lg
            label={InputLabels.SECOND_NAME}
          />
          {getFieldError('secondName')}
        </div>
        <div className="registrastion-info-step__row">
          <div className="login-form__item-input">
            <UiInput
              error={formState.errors.position}
              name="position"
              onChange={formState.handleChange}
              onInputBlur={formState.handleBlur}
              lg
              label={InputLabels.POSITION}
            />
            {getFieldError('position')}
          </div>
          <div className="login-form__item-input">
            <UiInput
              name="email"
              type="email"
              error={formState.errors.email}
              onChange={formState.handleChange}
              onInputBlur={formState.handleBlur}
              lg
              label={InputLabels.EMAIL}
            />
            {getFieldError('email')}
          </div>
        </div>
      </div>
      <div className="login-form__fildset">
        <h2 className="text-1 login-form__fildset-title ">
          {InfoStepLabels.COMPANY_DATA}
        </h2>
        <div className="registrastion-info-step__row">
          <div className="login-form__item-input">
            <UiInput
              name="inn"
              error={formState.errors.inn}
              value={formState.values.inn}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                e.target.value?.length <= 10 && formState.handleChange(e)
              }
              onInputBlur={formState.handleBlur}
              lg
              label={InputLabels.INN}
            />
            {getFieldError('inn')}
          </div>
          <div className="login-form__item-input">
            <UiInput
              name="ogrn"
              error={formState.errors.ogrn}
              onChange={formState.handleChange}
              onInputBlur={formState.handleBlur}
              lg
              label={InputLabels.OGRN}
            />
            {getFieldError('ogrn')}
          </div>
        </div>
        <div className="login-form__item-input">
          <UiInput
            name="companyName"
            error={formState.errors.companyName}
            onChange={formState.handleChange}
            onInputBlur={formState.handleBlur}
            lg
            label={InputLabels.COMPANY_NAME}
          />
          {getFieldError('companyName')}
        </div>
        <div className="login-form__item-input">
          <UiInput
            name="companyShortName"
            error={formState.errors.companyShortName}
            onChange={formState.handleChange}
            onInputBlur={formState.handleBlur}
            lg
            label={InputLabels.COMPANY_SHORT_NAME}
          />
          {getFieldError('companyShortName')}
        </div>
      </div>
      <div className="registrastion-info-step__footer">
        <FormFooter
          actions={
            <IconBtn
              disabled={
                !formState.isValid ||
                isLoadingInfoStep ||
                !formState.values.ogrn ||
                !formState.values.inn ||
                !formState.values.companyShortName?.trim() ||
                !formState.values.companyName?.trim() ||
                !formState.values.email?.trim() ||
                !formState.values.name?.trim() ||
                !formState.values.lastName?.trim() ||
                !formState.values.position?.trim()
              }
              onClick={() => formState.handleSubmit()}
              icon={UserIcon}
              title="Продолжить"
              lg
            />
          }
        />
        <p className="p p_font-size_12 p_color_grey">
          На этот email будут приходить уведомления об операциях на платформе
        </p>
      </div>
    </form>
  );
};

export default InfoStep;
